import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { GET_RATES } from "~/graphql/query";
import { GET_MATTER_SPECIFIC_RATES } from "~/graphql/query/get-matter-specific-rates";
import { Rate } from "~/design-system";

type UseRowRateOptionsParams = {
  row: any;
  currency: { symbol: string; code: string };
  matterId: number;
  clientId?: number;
  organisationalUnitId?: number;
  topDownRateOptions: {
    feeEarnerLevelId: number;
    order: number;
    rates: Pick<Rate, "id" | "description" | "amount">[];
  }[];
};

const isFeeEarnerRow = (row: any): boolean => Boolean(row?.feeEarner);

export function useRowRateOptions({
  row,
  currency,
  matterId,
  clientId,
  organisationalUnitId,
  topDownRateOptions,
}: UseRowRateOptionsParams) {
  // Fee earner rows should use live GET_RATES + matter specific rates
  const feeEarnerIdForQuery = row?.feeEarner?.id ?? row?.feeEarnerId ?? null;
  // Derive level from the fee earner, not the currently selected rate
  const feeEarnerLevelIdForQuery =
    row?.feeEarner?.feeEarnerLevel?.id ??
    row?.feeEarnerLevel?.id ??
    row?.rate?.feeEarnerLevel?.id ??
    row?.rate?.feeEarnerLevelId ??
    null;

  const getRatesVars = isFeeEarnerRow(row)
    ? {
        variables: {
          filter: {
            clientId,
            organisationalUnitId,
            feeEarnerId: feeEarnerIdForQuery,
            externalMatterId: undefined,
            feeEarnerLevelId: feeEarnerLevelIdForQuery,
            currencyCode: currency.code,
            matterId,
          },
        },
        skip: false,
      }
    : { variables: undefined as any, skip: true };

  const { data: ratesData, loading: loadingRates } = useQuery(GET_RATES, getRatesVars as any);
  const { data: matterSpecificRatesData, loading: loadingMatterRates } = useQuery(
    GET_MATTER_SPECIFIC_RATES,
    {
      variables: { matterId, currencyCode: currency.code },
      skip: !isFeeEarnerRow(row),
    }
  );

  const options: Pick<Rate, "id" | "description" | "amount">[] = useMemo(() => {
    const levelId =
      row?.feeEarner?.feeEarnerLevel?.id ??
      row?.rate?.feeEarnerLevel?.id ??
      row?.rate?.feeEarnerLevelId;
    const levelGroup = topDownRateOptions.find((g) => g.feeEarnerLevelId === levelId);
    const levelRates = levelGroup ? levelGroup.rates : row?.rate ? [row.rate] : [];

    if (isFeeEarnerRow(row)) {
      const rawFetched = [
        ...(ratesData?.getRates || []),
        ...(matterSpecificRatesData?.matterSpecificRates || []),
      ];
      const fetched = rawFetched.map((r: any) => ({
        id: r.id,
        amount: r.amount,
        description: r.description,
      }));
      // Ensure level rates are present
      const combinedMap = new Map<number, Pick<Rate, "id" | "description" | "amount">>();
      for (const r of [...fetched, ...levelRates]) {
        if (r && typeof r.id === "number")
          combinedMap.set(r.id, {
            id: r.id,
            amount: (r as any).amount,
            description: (r as any).description,
          });
      }
      return Array.from(combinedMap.values());
    }

    return levelRates;
  }, [row, ratesData?.getRates, matterSpecificRatesData?.matterSpecificRates, topDownRateOptions]);

  return {
    options,
    loading: loadingRates || loadingMatterRates,
  };
}

export { isFeeEarnerRow };
